import { useTranslation } from "react-i18next";
import { List } from "@mui/material";
import statusService from "api/statusService";
import tagsService from "api/tagsService";
import MessageCard from "components/MessageCard";
import PackageSkeleton from "components/PackageSkeleton";
import { IOrderObject } from "components/SortOrder/typings";
import KeysContext from "contexts/KeysContext";
import PackagesContext from "contexts/PackagesContext";
import { IAttachment } from "dto/IAttachment";
import { IPackage } from "dto/IPackage";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import NotFoundScreen from "components/NotFoundScreen";
import { useKeyPressEvent } from "react-use";
import classNames from "classnames";

export const PackageList = ({
  debouncedText,
  fetchNextPage,
  fetchPrevPage,
  customFilterId,
  searchText,
  isIncoming,
  onPreview,
  onDownload,
  firstElement,
  selectedElements,
  setSelectedElements,
  canSelect,
  selectMultiple,
  showSomething,
  customSortMethod,
  handleScroll,
  separatorRef,
  isLoading = false,
  remove,
  listFilter,
  status: stat,
  isFirstLoading = false,
  isInitialized,
  newPackagesCounter,
  onMessageClick,
  className,
  showBadgeOnIncomingMessage,
  isMobile = false,
}: {
  debouncedText: string;
  searchText: string;
  customFilterId?: string;
  isIncoming: boolean;
  onPreview?: (pack: IPackage, attachment: IAttachment) => void;
  onDownload: (pack: IPackage, attachment: IAttachment) => void;
  firstElement: IPackage | null;
  selectedElements: IPackage[];
  setSelectedElements: (packs: IPackage[]) => void;
  canSelect: boolean;
  selectMultiple: boolean;
  showSomething: boolean;
  customSortMethod?: string;
  handleScroll?: (e: any) => void;
  separatorRef?: any;
  isLoading?: boolean;
  remove?: () => void;
  listFilter?: IOrderObject;
  status?: string;
  fetchNextPage: () => void;
  fetchPrevPage: () => void;
  isFirstLoading?: boolean;
  isInitialized: boolean;
  newPackagesCounter: number;
  onMessageClick?: (message: IPackage) => void;
  className?: string;
  showBadgeOnIncomingMessage?: boolean;
  isMobile?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filterId, status: linkStatus } = useParams();
  const scrollWrapper = useRef(null);

  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchThreshold = 1000; // 1 second

  const { packages } = useContext(PackagesContext);

  const { keys } = useContext(KeysContext);

  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  useKeyPressEvent("ArrowDown", () => focusNextElement());
  useKeyPressEvent("ArrowUp", () => focusPrevelement());

  const focusNextElement = () => {
    if (!scrollWrapper.current) return;

    if (!scrollWrapper.current.contains(document.activeElement)) {
      return;
    }

    if (!selectedElements || selectedElements.length === 0) {
      if (packages.length > 0) {
        setSelectedElements([packages[0]]);
        navigateToPackage(packages[0]);
      }
    } else {
      const index = packages.indexOf(selectedElements[0]);

      if (index !== -1 && index + 1 < packages.length) {
        setSelectedElements([packages[index + 1]]);
        navigateToPackage(packages[index + 1]);
      }
    }
  };

  const focusPrevelement = () => {
    if (!scrollWrapper.current) return;

    if (!scrollWrapper.current.contains(document.activeElement)) {
      return;
    }

    if (!selectedElements || selectedElements.length === 0) {
      if (packages.length > 0) {
        setSelectedElements([packages[0]]);
        navigateToPackage(packages[0]);
      }
    } else {
      const index = packages.indexOf(selectedElements[0]);

      if (index !== -1 && index > 0) {
        setSelectedElements([packages[index - 1]]);
        navigateToPackage(packages[index - 1]);
      }
    }
  };

  const navigateToPackage = (msg: IPackage) => {
    const link = linkStatus
      ? linkStatus
      : isIncoming === true
      ? "incoming"
      : "outcoming";

    if (msg.Kind === 256) {
      navigate(
        `/packages/${filterId}/${link}/${msg.Kind}/${msg.Id}/preview-letter/${msg.Id}`,
      );
      return;
    }

    if (msg?.Thread?.Id && msg?.Thread?.Count > 1 /*|| isThread*/) {
      navigate(
        `/packages/thread/${filterId}/${link}/${msg.Kind}/${msg.Thread.Id}/${msg.Id}`,
      );
      return;
    }

    navigate(`/packages/${filterId}/${link}/${msg.Kind}/${msg.Id}`);
  };

  const handleLoadNextPage = (e: any) => {
    if (!separatorRef.current || isLoading) return;

    const rect = separatorRef.current.getBoundingClientRect();

    const isSeparatorVisible =
      rect.top * 0.5 < window.innerHeight && rect.bottom >= 0;

    const currentTime = Date.now();
    if (currentTime - lastFetchTime < fetchThreshold) return;

    if (isSeparatorVisible) {
      setLastFetchTime(currentTime);
      fetchNextPage();
    } else if (scrollWrapper.current.scrollTop < 10) {
      setLastFetchTime(currentTime);
      fetchPrevPage();
    }
  };

  //@ts-ignore
  const uniquePackages: IPackage[] =
    Array.from(new Set(packages.map((pkg) => pkg.Id))).map((id) => {
      return packages.find((pkg) => pkg.Id === id);
    }) ?? [];

  useEffect(() => {
    if (newPackagesCounter > 0 && scrollWrapper.current) {
      console.log("Scrolling to top");
      scrollWrapper.current.scrollTop = 0;
    }
  }, [newPackagesCounter]);

  if (isFirstLoading) {
    return (
      <div>
        {Array.from({ length: 100 }, (_, index) => (
          <PackageSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={classNames(styles.packageList, className)}>
      <List
        ref={scrollWrapper}
        onScroll={(e) => {
          handleLoadNextPage(e);
        }}
        sx={{
          width: "100%",
          position: "relative",
          overflow: "auto",
          maxHeight: "calc(100% )",
          "& ul": { padding: 0 },
        }}
      >
        {!uniquePackages.length &&
          !isFirstLoading &&
          !isLoading &&
          isInitialized && (
            <div style={{ height: "calc(100vh - 180px)" }}>
              <NotFoundScreen text={t("emptyStates.noResultsSuffix")} />
            </div>
          )}
        {uniquePackages
          .filter((f) => !f.isDelete)
          .map((pack) => {
            return (
              <div className={styles.packe} key={`msg_${pack.Id}`}>
                <MessageCard
                  scrollWrapper={scrollWrapper}
                  listFilter={listFilter}
                  key={pack.Id}
                  remove={remove}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  isFirst={
                    firstElement?.Id === pack.Id && selectedElements.length > 1
                  }
                  selectedElements={selectedElements}
                  setSelectedElements={setSelectedElements}
                  isSelectable={canSelect}
                  selectMultiple={selectMultiple}
                  showSomething={showSomething}
                  isIncoming={isIncoming}
                  keys={keys}
                  msg={pack}
                  statuses={statuses}
                  tags={tags}
                  decodedContent={undefined}
                  onClick={onMessageClick ? () => onMessageClick(pack) : null}
                  showBadgeOnIncomingMessage={showBadgeOnIncomingMessage}
                  isMobile={isMobile}
                />
              </div>
            );
          })}
        {!isFirstLoading && isLoading && isInitialized && (
          <PackageSkeleton key={"packageSkeleton_000"} />
        )}
        <div ref={separatorRef} style={{ height: "5px" }}></div>
      </List>
    </div>
  );
};
